import { OptionFieldModel } from "@/common/commonForms.ts";
import { Alert } from "@/components-v2/Alert";
import { Box, Grid, GridItem, VBox } from "@/components-v2/Box";
import { LinkButton } from "@/components-v2/Button";
import { Checkbox } from "@/components-v2/Checkbox.tsx";
import { Dialog } from "@/components-v2/Dialog";
import { MRadioButtonGroup } from "@/components-v2/RadioButton";
import { Tag } from "@/components-v2/Tag.tsx";
import { MTextInput } from "@/components-v2/TextInput";
import { useTranslation } from "@/stores/RootStoreContext";
import {
  InvitationRecipientModel,
  SendInvitationDialogStore,
} from "@/stores/SendInvitationDialogStore";
import { LocalizedTextEditor } from "@/text/LocalizedTextEditor";
import { Markdown } from "@/text/Markdown";
import { isProductionEnvironment } from "@/utils/environments";
import { adminSettings } from "@/utils/navigation";
import { observer } from "mobx-react";
import { Fragment, ReactElement } from "react";

export const SendInvitationDialog = observer(function InviteUserDialog({
  store,
}: {
  store: SendInvitationDialogStore;
}) {
  const { t } = useTranslation();

  const handleSubmit = store.showSubmitButton ? store.submitInviteUserDialog : undefined;
  return (
    <Dialog
      title={store.title}
      isSubmitting={store.isLoading}
      submitLabel={t("invite-user.send-invite")}
      cancelLabel={t(store.closeLabel)}
      onSubmit={handleSubmit}
      onCancel={store.onClose}
      isSubmitDisabled={store.isSubmitDisabled}
    >
      <VBox gap={"md"}>
        {!isProductionEnvironment() && store.adminInfo && (
          <Markdown value={t(store.adminInfo)} enableLinks />
        )}
        {store.requiresShareholderPortal && (
          <Alert
            type={"warning"}
            message={{
              key: "shareholders.notify.portal-not-enabled",
              params: {
                link: adminSettings(store.organisationNumber),
              },
            }}
          />
        )}
        {store.description && <Box>{t(store.description)}</Box>}
        {store.infoMessage && (
          <Alert
            message={<Markdown value={t(store.infoMessage)} enableLinks />}
            type={"info"}
            icon={"info"}
          />
        )}
        <InvitationsPersonalNote store={store} />
        <RecipientView store={store} />
        {store.error && <Alert type={"error"} message={<>{store.error}</>} />}
      </VBox>
    </Dialog>
  );
});

const InvitationsPersonalNote = observer(({ store }: { store: SendInvitationDialogStore }) => {
  const { t } = useTranslation();
  return (
    <VBox>
      <Box gap={"md"} marginBottom={"md"}>
        <div>{t("shareholders.notify.email.language")}</div>
        <MRadioButtonGroup model={store.language} horizontal />
      </Box>
      <strong>{t("invite.user.email.title")}</strong>
      <em>{t(store.commonEmailText, { lang: store.language.value })}</em>
      <Box
        justifyContent={store.isPersonalNoteEnabled ? "space-between" : "start"}
        marginTop={"md"}
      >
        {store.isPersonalNoteEnabled && <div>{t("invite-user.personal.note")}</div>}
        <LinkButton
          label={store.isPersonalNoteEnabled ? t("remove") : t("invite-user.add.personal.note")}
          icon={store.isPersonalNoteEnabled ? "trash" : "plus"}
          onClick={store.togglePersonalNote}
        />
      </Box>
      {store.isPersonalNoteEnabled && <LocalizedTextEditor model={store.personalNote} autofocus />}
    </VBox>
  );
});

const RecipientView = observer(({ store }: { store: SendInvitationDialogStore }) => {
  const { t } = useTranslation();
  return store.recipients ? (
    <RecipientEmailCheckBoxes model={store.recipients} isMissingEmail={store.isMissingEmail} />
  ) : (
    <VBox marginTop={"md"}>
      <strong>{t("invite-dialog.send-to")}</strong>
      <MTextInput model={store.email} label={t("invite-user.email")} />
    </VBox>
  );
});

const RecipientEmailCheckBoxes = observer(
  ({
    model,
    isMissingEmail,
  }: {
    model: OptionFieldModel<InvitationRecipientModel>;
    isMissingEmail: boolean;
  }) => {
    const { t } = useTranslation();
    const recipientHasNeverLoggedIn = model.options.filter((r) => r.lastLogin === undefined);
    const isNeverLoggedInSelected = model.options.every(
      (r) => model.isSelected(r) === (r.lastLogin === undefined),
    );

    return (
      <VBox gap={"md"}>
        <strong>{t("invite-dialog.send-to")}</strong>
        <Box>
          <LinkButton
            label={t("never-logged-in")}
            onClick={() => model.setSelected(recipientHasNeverLoggedIn)}
            disabled={isNeverLoggedInSelected}
          />
          {"/"}
          <LinkButton
            label={t("select-all")}
            onClick={() => model.toggleAll(true)}
            disabled={model.isAllSelected}
          />
          {"/"}
          <LinkButton
            label={t("select-none")}
            onClick={() => model.toggleAll(false)}
            disabled={model.isNoneSelected}
          />
        </Box>
        {model.error && <Alert message={t(model.error)} type={"error"} />}
        {isMissingEmail && <Alert message={t("invite-user.missing-emails")} type={"warning"} />}
        <Grid maxWidth={"xl"} alignItems={"center"}>
          {model.options.map((recipient) => (
            <Fragment key={recipient.id}>
              <GridItem xs={8}>
                <Checkbox
                  checked={model.isSelected(recipient)}
                  label={
                    <RecipientLastLoginLabel
                      name={recipient.name}
                      lastLogin={recipient.lastLogin}
                    />
                  }
                  onChange={() => model.toggle(recipient)}
                />
              </GridItem>
              <GridItem xs={4}>
                <MTextInput
                  placeholder={t("email")}
                  model={recipient.email}
                  disabled={!model.isSelected(recipient)}
                />
              </GridItem>
            </Fragment>
          ))}
        </Grid>
      </VBox>
    );
  },
);

export function RecipientLastLoginLabel({
  name,
  lastLogin,
}: {
  name: string;
  lastLogin: string | undefined;
}): ReactElement {
  if (lastLogin === undefined) {
    return (
      <Box alignItems={"center"}>
        <div>{name}</div>
        <Tag color={"neutral"} label={{ key: "never-logged-in" }} />
      </Box>
    );
  } else {
    return <div>{name}</div>;
  }
}
